import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { BookOpen, Plus, Pencil, Trash2, Eye, EyeOff, Upload, ChevronUp, ChevronDown, X } from "lucide-react";
import { cases as staticCases } from "../data/cases";
import { invalidateCases } from "../lib/cases";
import { useLang, AZ } from "../i18n";
import { isDirectImageUrl, storageMediaUrl } from "../lib/media";
import { SafeImage } from "./Media";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls =
  "w-full rounded-xl border border-white/15 bg-surface px-4 py-3 text-sm text-white outline-none transition-colors focus:border-gold";
const labelCls = "mb-1.5 block font-mono text-[11px] uppercase tracking-[0.2em] text-white/60";

const lines = (v) => (v || "").split("\n").map((s) => s.trim()).filter(Boolean);
const pairs = (v, a, b) =>
  lines(v).map((l) => {
    const [first, ...rest] = l.split("|");
    return { [a]: x(first), [b]: x(rest.join("|")) };
  });
const x = (v) => (v || "").trim();
const joinPairs = (arr, a, b) => (arr || []).map((o) => `${o[a] || ""} | ${o[b] || ""}`).join("\n");
const az = (s) => (s && AZ[s]) || "";

const azLinesFrom = (arr) => (arr || []).map((s) => AZ[s] || "").filter(Boolean).join("\n");
const azPairsFrom = (arr, a, b) =>
  (arr || [])
    .map((o) => ({ [a]: AZ[o[a]] || "", [b]: AZ[o[b]] || "" }))
    .filter((o) => o[a] || o[b]);

const parseVideoRow = (entry = "") => {
  const [raw, ...rest] = String(entry || "").split("|");
  return { url: raw.trim(), thumbnail: rest.join("|").trim() };
};
const normalizeVideoEntry = (entry = {}) => {
  const video = storageMediaUrl(String(entry.url || "").trim());
  const thumb = storageMediaUrl(String(entry.thumbnail || "").trim());
  if (!video) return "";
  return thumb ? `${video} | ${thumb}` : video;
};
const mergeMedia = (base = [], custom = []) => Array.from(new Set([...(base || []), ...(custom || [])].filter(Boolean)));

const toForm = (c) => ({
  slug: c.slug || "",
  client: c.client || "",
  sector: c.sector || "Real Estate",
  segment: c.segment || "",
  title: c.title || "",
  title_az: c.title_az || az(c.title),
  year: c.year || "",
  yearConfirmed: c.yearConfirmed ?? false,
  timeframe: c.timeframe || "",
  summary: c.summary || "",
  summary_az: c.summary_az || az(c.summary),
  context: c.context || "",
  context_az: c.context_az || az(c.context),
  challenges: (c.challenges || []).join("\n"),
  challenges_az: Array.isArray(c.challenges_az) && c.challenges_az.length ? c.challenges_az.join("\n") : azLinesFrom(c.challenges),
  approach: joinPairs(c.approach, "t", "d"),
  approach_az: Array.isArray(c.approach_az) && c.approach_az.length ? joinPairs(c.approach_az, "t", "d") : joinPairs(azPairsFrom(c.approach, "t", "d"), "t", "d"),
  creative: c.creative || "",
  creative_az: c.creative_az || az(c.creative),
  impact: joinPairs(c.impact, "v", "l"),
  impact_az: Array.isArray(c.impact_az) && c.impact_az.length
    ? joinPairs(c.impact_az, "v", "l")
    : joinPairs((c.impact || []).map((o) => ({ v: o.v || "", l: AZ[o.l] || "" })).filter((o) => o.v || o.l), "v", "l"),
  outcome: c.outcome || "",
  outcome_az: c.outcome_az || az(c.outcome),
  note: c.note || "",
  note_az: c.note_az || az(c.note),
  quoteText: c.quote?.text || "",
  quoteBy: c.quote?.by || "",
  quoteRole: c.quote?.role || "",
  quoteOrg: c.quote?.org || "",
  quoteText_az: c.quote_az?.text || az(c.quote?.text),
  quoteBy_az: c.quote_az?.by || c.quote?.by || "",
  quoteRole_az: c.quote_az?.role || az(c.quote?.role),
  quoteOrg_az: c.quote_az?.org || c.quote?.org || "",
  scope: (c.scope || []).join(", "),
  services: (c.services || []).join(", "),
  cover: c.cover || "",
  logo: c.logo || "",
  gallery: c.gallery || [],
  videos: (c.videos || []).map(parseVideoRow),
  real: c.real ?? true,
  ongoing: c.ongoing ?? false,
  published: c.published ?? true,
});

const fromForm = (f) => ({
  slug: f.slug.trim().toLowerCase(),
  client: f.client.trim(),
  sector: f.sector,
  segment: f.segment.trim() || null,
  title: f.title.trim(),
  title_az: f.title_az.trim(),
  year: f.year.trim(),
  yearConfirmed: f.yearConfirmed,
  contentRevision: "master-2026-bilingual",
  timeframe: f.timeframe.trim(),
  summary: f.summary.trim(),
  summary_az: f.summary_az.trim(),
  context: f.context.trim(),
  context_az: f.context_az.trim(),
  challenges: lines(f.challenges),
  challenges_az: lines(f.challenges_az),
  approach: pairs(f.approach, "t", "d"),
  approach_az: pairs(f.approach_az, "t", "d"),
  creative: f.creative.trim(),
  creative_az: f.creative_az.trim(),
  impact: pairs(f.impact, "v", "l"),
  impact_az: pairs(f.impact_az, "v", "l"),
  outcome: f.outcome.trim(),
  outcome_az: f.outcome_az.trim(),
  note: f.note.trim(),
  note_az: f.note_az.trim(),
  quote: f.quoteText.trim() ? { text: f.quoteText.trim(), by: f.quoteBy.trim(), role: f.quoteRole.trim(), org: f.quoteOrg.trim() } : null,
  quote_az: f.quoteText_az.trim() ? { text: f.quoteText_az.trim(), by: f.quoteBy_az.trim(), role: f.quoteRole_az.trim(), org: f.quoteOrg_az.trim() } : null,
  scope: f.scope.split(",").map((s) => s.trim()).filter(Boolean),
  services: f.services.split(",").map((s) => s.trim()).filter(Boolean),
  cover: storageMediaUrl(f.cover.trim()),
  logo: storageMediaUrl(f.logo.trim()),
  gallery: f.gallery.filter(Boolean).map(storageMediaUrl),
  videos: (f.videos || []).map(normalizeVideoEntry).filter(Boolean),
  real: f.real,
  ongoing: f.ongoing,
  published: f.published,
});

const Field = ({ id, label, hint, children }) => (
  <div>
    <label htmlFor={id} className={labelCls}>{label}</label>
    {children}
    {hint && <p className="mt-1 text-[11px] text-mist">{hint}</p>}
  </div>
);

async function uploadMedia(file) {
  const fd = new FormData();
  fd.append("file", file);
  const r = await fetch(`${API}/admin/media`, { method: "POST", body: fd, credentials: "include" });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.detail || "Upload failed");
  const rawUrl = data.url || (data.id ? `/api/media/${data.id}` : "");
  if (!rawUrl) throw new Error("Upload succeeded but no media URL was returned");
  // Persist a deployment-safe relative /api/media/... URL in MongoDB.
  // mediaSrc() resolves it only when rendering a preview.
  return storageMediaUrl(rawUrl);
}


const UploadButton = ({ accept = "image/*", multiple = false, label, onUploaded }) => {
  const [busy, setBusy] = useState(false);
  return (
    <label className={`inline-flex cursor-pointer items-center gap-2 rounded-full border border-white/20 px-4 py-2 text-xs font-semibold text-white transition hover:border-gold hover:text-gold ${busy ? "pointer-events-none opacity-50" : ""}`}>
      <Upload className="h-4 w-4" /> {busy ? "Uploading…" : label}
      <input
        className="sr-only"
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={async (e) => {
          const files = Array.from(e.target.files || []);
          if (!files.length) return;
          setBusy(true);
          try {
            const urls = [];
            for (const file of files) urls.push(await uploadMedia(file));
            onUploaded(multiple ? urls : urls[0]);
            toast.success(files.length > 1 ? `${files.length} files uploaded.` : "File uploaded.");
          } catch (err) {
            toast.error(err.message);
          } finally {
            setBusy(false);
            e.target.value = "";
          }
        }}
      />
    </label>
  );
};

const CaseForm = ({ initial, onCancel, onSaved }) => {
  const { lang } = useLang();
  const [f, setF] = useState(() => toForm(initial));
  const [busy, setBusy] = useState(false);
  const [editLang, setEditLang] = useState(lang === "en" ? "en" : "az");
  const A = (azText, enText) => (lang === "az" ? azText : enText);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const setValue = (k, value) => setF((prev) => ({ ...prev, [k]: value }));
  const key = (base) => (editLang === "az" ? `${base}_az` : base);
  const L = (azText, enText) => (editLang === "az" ? azText : enText);
  const text = (k, label, rows = 3, hint) => (
    <Field id={`cs-${k}`} label={label} hint={hint}>
      <textarea id={`cs-${k}`} data-testid={`case-form-${k}`} rows={rows} value={f[k] || ""} onChange={set(k)} className={inputCls} />
    </Field>
  );
  const input = (k, label, hint) => (
    <Field id={`cs-${k}`} label={label} hint={hint}>
      <input id={`cs-${k}`} data-testid={`case-form-${k}`} value={f[k] || ""} onChange={set(k)} className={inputCls} />
    </Field>
  );

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = fromForm(f);
      const isDbRecord = Boolean(initial.id);
      const originalSlug = (initial.slug || payload.slug || "").trim().toLowerCase();
      const endpoint = isDbRecord
        ? `${API}/admin/case-studies/${initial.id}`
        : `${API}/admin/case-studies/by-slug/${encodeURIComponent(originalSlug)}`;

      const r = await fetch(endpoint, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Please check the required fields");
      invalidateCases();
      window.dispatchEvent(new Event("yuxel:cases-updated"));
      toast.success(isDbRecord ? A("Layihə yeniləndi və saytda aktivdir.", "Case study updated and is live.") : A("Layihə yadda saxlanıldı və redaktə edilə bilən versiya yaradıldı.", "Case study saved and made editable."));
      onSaved(data);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const moveGallery = (index, delta) => {
    const next = [...f.gallery];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setValue("gallery", next);
  };
  const updateVideo = (index, patch) => setValue("videos", f.videos.map((v, i) => (i === index ? { ...v, ...patch } : v)));
  const moveVideo = (index, delta) => {
    const next = [...f.videos];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setValue("videos", next);
  };

  return (
    <form onSubmit={save} data-testid="case-form" className="mt-6 space-y-6 border border-gold/30 bg-surface/60 p-5 sm:p-6">
      <div className="grid gap-4 sm:grid-cols-2">
        {input("client", A("Müştəri / layihə *", "Client / project *"))}
        <Field id="cs-slug" label="URL slug *" hint={A("Mövcud layihədə URL qorunur. Yeni layihədə kiçik hərflər, rəqəmlər və tire istifadə edin.", "Existing project URLs are protected. For a new project use lowercase letters, numbers and dashes.")}>
          <input id="cs-slug" data-testid="case-form-slug" value={f.slug || ""} onChange={set("slug")} readOnly={Boolean(initial.slug)} className={`${inputCls} ${initial.slug ? "cursor-not-allowed opacity-60" : ""}`} />
        </Field>
        <Field id="cs-sector" label={A("Sektor", "Sector")}>
          <select id="cs-sector" data-testid="case-form-sector" value={f.sector} onChange={set("sector")} className={inputCls}>
            {["Real Estate", "Construction", "Entertainment", "Hospitality"].map((s) => <option key={s} value={s} className="bg-panel">{s}</option>)}
          </select>
        </Field>
        {input("segment", A("Seqment", "Segment label"), A("istəyə bağlı", "optional, e.g. Commercial Real Estate"))}
        {input("year", A("İl", "Year"), "e.g. 2024–2025")}
        {input("timeframe", A("Müddət", "Timeframe"), "e.g. 10 months")}
      </div>

      <div className="flex items-center gap-2 border-y border-white/10 py-4">
        <span className="mr-2 font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">{A("Mətn dili", "Content language")}</span>
        {["az", "en"].map((l) => (
          <button key={l} type="button" onClick={() => setEditLang(l)} className={`rounded-full px-5 py-2 text-sm font-semibold uppercase transition ${editLang === l ? "bg-white text-black" : "border border-white/20 text-white/60 hover:text-white"}`}>{l}</button>
        ))}
      </div>

      {input(key("title"), L("Başlıq *", "Headline *"))}
      {text(key("summary"), L("Hero xülasəsi", "Hero summary"), 2, L("Layihənin yuxarı hissəsində görünən qısa mətn.", "Short text shown in the project hero."))}

      <div className="space-y-4 border border-gold/25 bg-black/15 p-4 sm:p-5">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-gold">{L("Saytda görünən əsas 3 mətn", "3 primary texts shown on the site")}</p>
          <p className="mt-1 text-xs text-mist">{L("Layihə səhifəsində Problem → Nə etdik → Nəticə hissəsində məhz bu üç sahə göstərilir.", "These three fields feed the visible Problem → What we did → Result section.")}</p>
        </div>
        {text(key("context"), L("1. Problem", "1. Problem"), 3, L("Qısa və konkret yazın.", "Keep it short and specific."))}
        {text(key("creative"), L("2. Nə etdik", "2. What we did"), 3, L("Gördüyünüz əsas işi bir abzasda yazın.", "Describe the core work in one paragraph."))}
        {text(key("outcome"), L("3. Nəticə", "3. Result"), 3, L("Yalnız təsdiqlənən nəticəni yazın.", "Use only confirmed outcomes."))}
      </div>

      <details className="border border-white/10 bg-white/[0.02] p-4">
        <summary className="cursor-pointer font-mono text-[11px] uppercase tracking-[0.2em] text-white/55">{L("Əlavə / arxiv məlumatları", "Additional / archive fields")}</summary>
        <div className="mt-5 space-y-4">
          {text(key("challenges"), L("Əlavə problem maddələri", "Additional challenges"), 4, L("hər sətrə bir maddə", "one per line"))}
          {text(key("approach"), L("Əlavə yanaşma addımları", "Additional approach steps"), 5, L("hər sətrə: Addım | Açıqlama", "one per line: Step title | Description"))}
          {text(key("impact"), L("Əlavə nəticə metrikləri", "Additional result metrics"), 4, L("hər sətrə: Dəyər | Açıqlama", "one per line: Value | Label"))}
          {input(key("note"), L("Qeyd / məxfilik qeydi", "Confidentiality / footnote"))}
          <div className="grid gap-4 sm:grid-cols-2">
            {text(key("quoteText"), L("Sitat", "Quote"), 2)}
            <div className="grid gap-3">
              {input(key("quoteBy"), L("Sitat — ad", "Quote — name"))}
              {input(key("quoteRole"), L("Sitat — vəzifə", "Quote — role"))}
              {input(key("quoteOrg"), L("Sitat — təşkilat", "Quote — organisation"))}
            </div>
          </div>
        </div>
      </details>

      <div className="grid gap-4 sm:grid-cols-2">
        {input("scope", "Scope", "comma-separated")}
        {input("services", "Related services", "comma-separated, must match service names")}
      </div>

      <div className="grid gap-6 border-t border-white/10 pt-6 lg:grid-cols-2">
        <div>
          <p className={labelCls}>{A("Layihə loqosu", "Project logo")}</p>
          <div className="flex flex-wrap items-center gap-3">
            <UploadButton label={A("Loqo yüklə", "Upload logo")} onUploaded={(url) => setValue("logo", url)} />
            {f.logo && <button type="button" onClick={() => setValue("logo", "")} className="rounded-full border border-white/20 px-4 py-2 text-xs text-white/70 hover:border-red-400 hover:text-red-300">Remove</button>}
          </div>
          {f.logo && <div className="mt-3 flex h-24 items-center justify-center border border-white/10 bg-white/5 p-4"><SafeImage src={f.logo} alt="Project logo preview" className="max-h-full max-w-full object-contain" fallbackClassName="h-full w-full" /></div>}
          <details className="mt-3"><summary className="cursor-pointer text-xs text-white/45">Advanced: logo URL</summary><div className="mt-2">{input("logo", "Logo URL")}</div></details>
        </div>

        <div>
          <p className={labelCls}>{A("Qapaq şəkli", "Cover image")}</p>
          <div className="flex flex-wrap items-center gap-3">
            <UploadButton label={A("Qapaq şəkli yüklə", "Upload cover image")} onUploaded={(url) => setValue("cover", url)} />
            {f.cover && <button type="button" onClick={() => setValue("cover", "")} className="rounded-full border border-white/20 px-4 py-2 text-xs text-white/70 hover:border-red-400 hover:text-red-300">Remove</button>}
          </div>
          {f.cover && <SafeImage src={f.cover} alt="Cover preview" className="mt-3 aspect-video w-full border border-white/10 bg-black/20 object-contain" fallbackClassName="mt-3 aspect-video w-full border border-white/10" />}
          {f.cover && /^https?:\/\//i.test(f.cover) && !isDirectImageUrl(f.cover) && (
            <p className="mt-2 text-xs text-amber-300">This looks like a webpage link rather than a direct image file. Use the upload button or paste a direct image URL.</p>
          )}
          <details className="mt-3"><summary className="cursor-pointer text-xs text-white/45">Advanced: cover URL</summary><div className="mt-2">{input("cover", "Cover image URL")}</div></details>
        </div>
      </div>

      <div className="border-t border-white/10 pt-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div><p className={labelCls}>{A("Seçilmiş işlər / qalereya", "Selected work / gallery")}</p><p className="text-xs text-mist">{A("Real kampaniya, post, təqdimat və layihə vizuallarını yükləyin. Oxlarla sıralayın.", "Upload real campaign, post, presentation and project visuals. Reorder them with the arrows.")}</p></div>
          <UploadButton label={A("Şəkilləri yüklə", "Upload gallery images")} multiple onUploaded={(urls) => setValue("gallery", [...f.gallery, ...urls])} />
        </div>
        {f.gallery.length > 0 && (
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {f.gallery.map((url, i) => (
              <div key={`${url}-${i}`} className="relative border border-white/10 bg-panel p-2">
                <SafeImage src={url} alt={`Gallery preview ${i + 1}`} className="h-52 w-full bg-black/20 object-contain" fallbackClassName="h-52 w-full" />
                <div className="mt-2 flex items-center justify-between gap-2">
                  <span className="truncate text-[10px] text-white/40">{i + 1}</span>
                  <div className="flex gap-1">
                    <button type="button" onClick={() => moveGallery(i, -1)} className="rounded border border-white/15 p-1 text-white/60 hover:text-white"><ChevronUp className="h-3.5 w-3.5" /></button>
                    <button type="button" onClick={() => moveGallery(i, 1)} className="rounded border border-white/15 p-1 text-white/60 hover:text-white"><ChevronDown className="h-3.5 w-3.5" /></button>
                    <button type="button" onClick={() => setValue("gallery", f.gallery.filter((_, idx) => idx !== i))} className="rounded border border-white/15 p-1 text-red-300 hover:border-red-400"><X className="h-3.5 w-3.5" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="border-t border-white/10 pt-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className={labelCls}>{A("Video / Reel işləri", "Video / Reel work")}</p>
            <p className="text-xs text-mist">{A("Hər videoya qapaq şəkli əlavə edin ki, video açılmadan əvvəl içində nə olduğu görünsün.", "Add a thumbnail to each video so visitors can see the content before pressing play.")}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <UploadButton accept="video/mp4,video/webm" label={A("Video yüklə", "Upload video")} onUploaded={(url) => setValue("videos", [...f.videos, { url, thumbnail: "" }])} />
            <button type="button" onClick={() => setValue("videos", [...f.videos, { url: "", thumbnail: "" }])} className="rounded-full border border-white/20 px-4 py-2 text-xs font-semibold text-white hover:border-gold hover:text-gold">{A("Video linki əlavə et", "Add video link")}</button>
          </div>
        </div>
        {f.videos.length > 0 && (
          <div className="mt-4 space-y-3">
            {f.videos.map((video, i) => (
              <div key={`${video.url}-${i}`} className="grid gap-3 border border-white/10 bg-panel p-3 lg:grid-cols-[160px_1fr_auto] lg:items-center">
                <div className="flex aspect-video items-center justify-center overflow-hidden bg-black/30">
                  {video.thumbnail ? <SafeImage src={video.thumbnail} alt={`Video thumbnail ${i + 1}`} className="h-full w-full object-contain" fallbackClassName="h-full w-full" /> : <span className="px-3 text-center text-[10px] uppercase tracking-[.12em] text-white/35">{A("Qapaq yoxdur", "No thumbnail")}</span>}
                </div>
                <div className="grid gap-2 sm:grid-cols-2">
                  <Field id={`cs-video-${i}`} label={A("Video URL", "Video URL")}><input id={`cs-video-${i}`} value={video.url || ""} onChange={(e) => updateVideo(i, { url: e.target.value })} className={inputCls} placeholder="YouTube, Vimeo or uploaded /api/media/..." /></Field>
                  <Field id={`cs-video-thumb-${i}`} label={A("Qapaq şəkli", "Thumbnail / poster")}><input id={`cs-video-thumb-${i}`} value={video.thumbnail || ""} onChange={(e) => updateVideo(i, { thumbnail: e.target.value })} className={inputCls} placeholder="/api/media/... or direct image URL" /></Field>
                  <div className="sm:col-span-2"><UploadButton label={A("Qapaq şəkli yüklə", "Upload thumbnail")} onUploaded={(url) => updateVideo(i, { thumbnail: url })} /></div>
                </div>
                <div className="flex gap-1 lg:flex-col">
                  <button type="button" onClick={() => moveVideo(i, -1)} className="rounded border border-white/15 p-2 text-white/60 hover:text-white"><ChevronUp className="h-4 w-4" /></button>
                  <button type="button" onClick={() => moveVideo(i, 1)} className="rounded border border-white/15 p-2 text-white/60 hover:text-white"><ChevronDown className="h-4 w-4" /></button>
                  <button type="button" onClick={() => setValue("videos", f.videos.filter((_, idx) => idx !== i))} className="rounded border border-red-500/30 p-2 text-red-300 hover:border-red-400"><X className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-6 border-t border-white/10 pt-4 text-sm text-white">
        {[["yearConfirmed", "Year verified"], ["real", "Client work"], ["ongoing", "In progress"], ["published", "Published"]].map(([k, l]) => (
          <label key={k} className="flex items-center gap-2"><input type="checkbox" data-testid={`case-form-${k}`} checked={f[k]} onChange={set(k)} className="h-4 w-4 accent-gold" /> {l}</label>
        ))}
      </div>

      <div className="flex flex-wrap gap-3">
        <button type="submit" data-testid="case-form-save" disabled={busy} className="rounded-full bg-gold px-6 py-2.5 text-sm font-semibold text-ink hover:bg-champagne disabled:opacity-50">{busy ? A("Saxlanılır…", "Saving…") : A("Layihəni yadda saxla", "Save case study")}</button>
        <button type="button" data-testid="case-form-cancel" onClick={onCancel} className="rounded-full border border-white/20 px-5 py-2.5 text-sm font-semibold text-white hover:border-gold hover:text-gold">{A("Ləğv et", "Cancel")}</button>
      </div>
    </form>
  );
};

export const CaseStudiesAdmin = () => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    const r = await fetch(`${API}/admin/case-studies`, { credentials: "include", cache: "no-store" });
    if (!r.ok) return;
    const docs = await r.json();
    setItems(docs.map((c) => {
      const approved = staticCases.find((s) => s.slug === c.slug) || {};
      // DB values are authoritative for editable records. This is important
      // when the admin removes/reorders media or intentionally clears a field.
      return {
        ...approved,
        ...c,
        cover: c.cover ?? approved.cover ?? "",
        logo: c.logo ?? approved.logo ?? "",
        gallery: Array.isArray(c.gallery) ? c.gallery : (approved.gallery || []),
        videos: Array.isArray(c.videos) ? c.videos : (approved.videos || []),
        published: c.published,
      };
    }));
  }, []);

  useEffect(() => { load(); }, [load]);

  const dbSlugs = new Set(items.map((c) => c.slug));
  const builtIn = staticCases.filter((c) => !dbSlugs.has(c.slug));

  const save = async (c, patch) => {
    const r = await fetch(`${API}/admin/case-studies/${c.id}`, {
      method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify({ ...c, ...patch }),
    });
    if (!r.ok) return toast.error("Update failed");
    invalidateCases();
    window.dispatchEvent(new Event("yuxel:cases-updated"));
    load();
  };

  const remove = async (c) => {
    if (!window.confirm("Bu layihəni silmək istədiyinizə əminsiniz? Bu əməliyyat geri qaytarılmaya bilər.")) return;
    const r = await fetch(`${API}/admin/case-studies/${c.id}`, { method: "DELETE", credentials: "include" });
    if (!r.ok) return toast.error("Delete failed");
    invalidateCases();
    window.dispatchEvent(new Event("yuxel:cases-updated"));
    toast.success("Case study deleted.");
    if (editing?.id === c.id) setEditing(null);
    load();
  };

  const makeEditable = (s) => setEditing({ ...s, quote: s.quote || null, published: true });

  return (
    <section data-testid="case-studies-admin" className="mt-10 border border-white/10 bg-panel p-6 sm:p-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><p className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.3em] text-gold"><BookOpen className="h-4 w-4" /> Case studies</p><p className="mt-2 text-xs text-mist">Edit AZ/EN copy, upload logos and project media, publish or delete projects.</p></div>
        <button type="button" data-testid="case-new-button" onClick={() => setEditing({})} className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2 text-sm font-semibold text-ink hover:bg-champagne"><Plus className="h-4 w-4" /> New case study</button>
      </div>

      {editing && <CaseForm initial={editing} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}

      <ul className="mt-6 divide-y divide-white/10" data-testid="case-admin-list">
        {items.length === 0 && <li className="py-6 text-sm text-mist" data-testid="case-admin-empty">No editable case studies yet — create one or make a built-in case editable below.</li>}
        {items.map((c) => (
          <li key={c.id} data-testid={`case-admin-row-${c.slug}`} className="flex flex-wrap items-center justify-between gap-3 py-4">
            <div className="min-w-0"><p className="truncate text-sm font-semibold text-white">{c.client} <span className="font-normal text-white/50">· /{c.slug}</span></p><p className="truncate text-xs text-mist">{c.title}</p></div>
            <div className="flex flex-wrap items-center gap-2">
              <span className={`rounded-full px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.15em] ${c.published ? "bg-gold/15 text-gold" : "bg-white/10 text-white/60"}`}>{c.published ? "Published" : "Draft"}</span>
              <button type="button" data-testid={`case-toggle-${c.slug}`} onClick={() => save(c, { published: !c.published })} title={c.published ? "Unpublish" : "Publish"} className="rounded-full border border-white/15 p-2 text-white/70 hover:border-gold hover:text-gold">{c.published ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
              <button type="button" data-testid={`case-edit-${c.slug}`} onClick={() => setEditing(c)} className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-2 text-xs text-white/70 hover:border-gold hover:text-gold"><Pencil className="h-4 w-4" /> Edit</button>
              <button type="button" data-testid={`case-delete-${c.slug}`} onClick={() => remove(c)} className="inline-flex items-center gap-1.5 rounded-full border border-red-500/30 px-3 py-2 text-xs text-red-300 hover:border-red-400 hover:bg-red-500/10"><Trash2 className="h-4 w-4" /> Layihəni sil</button>
            </div>
          </li>
        ))}
      </ul>

      {builtIn.length > 0 && (
        <div className="mt-8 border-t border-white/10 pt-6">
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/50">Built-in case studies (from code)</p>
          <ul className="mt-3 flex flex-wrap gap-2" data-testid="case-builtin-list">
            {builtIn.map((s) => <li key={s.slug}><button type="button" data-testid={`case-make-editable-${s.slug}`} onClick={() => makeEditable(s)} className="rounded-full border border-white/15 px-3 py-1.5 text-xs text-white/80 hover:border-gold hover:text-gold">{s.client} → make editable</button></li>)}
          </ul>
          <p className="mt-3 text-[11px] text-mist">Opening a built-in case pre-fills its existing Azerbaijani translations where available. Saving copies it to the database.</p>
        </div>
      )}
    </section>
  );
};
